import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { type ReactNode, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Dialog,
  Pagination,
  Skeleton,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
  useToast,
} from "@rumbledirect/ui-v2";
import { RequirePermission } from "@/currentUser/RequirePermission";
import { useGetAdminMobilityCategories } from "@/lib/api/generated/mobility-category/mobility-category";
import type { MobilityCategory } from "@/mobilityCategories/mobilityCategory";
import { toMobilityCategoryInput } from "@/mobilityCategories/mobilityCategoryForm";
import { MobilityCategorySheet } from "@/mobilityCategories/MobilityCategorySheet";
import { useCreateMobilityCategory } from "@/mobilityCategories/useCreateMobilityCategory";
import { useDeleteMobilityCategory } from "@/mobilityCategories/useDeleteMobilityCategory";
import { useUpdateMobilityCategory } from "@/mobilityCategories/useUpdateMobilityCategory";
import { TopbarActions } from "@/shell/TopbarActions";

const CREATE_PERMISSION = "admin_mobility_category_create";
const UPDATE_PERMISSION = "admin_mobility_category_update";
const DELETE_PERMISSION = "admin_mobility_category_delete";

const ITEMS_PER_PAGE = 20;
const SKELETON_ROW_KEYS = Array.from(
  { length: 5 },
  (_, index) => `mobility-category-skeleton-${index}`,
);

interface MobilityCategoriesSearch {
  readonly page: number;
}

export const Route = createFileRoute("/_authenticated/mobility-categories")({
  validateSearch: (
    search: Record<string, unknown>,
  ): MobilityCategoriesSearch => {
    const page = Number(search.page);
    return { page: Number.isInteger(page) && page >= 1 ? page : 1 };
  },
  component: MobilityCategoriesPage,
});

function MobilityCategoriesPage(): ReactNode {
  const { page } = Route.useSearch();
  const navigate = Route.useNavigate();
  const toast = useToast();

  const { data, isPending, isPlaceholderData, isError, refetch } =
    useGetAdminMobilityCategories(
      { page },
      { query: { placeholderData: keepPreviousData } },
    );

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<MobilityCategory | undefined>(
    undefined,
  );
  const [pendingDelete, setPendingDelete] = useState<
    MobilityCategory | undefined
  >(undefined);

  const createMutation = useCreateMobilityCategory();
  const updateMutation = useUpdateMobilityCategory(editing?.id ?? "");
  const deleteMutation = useDeleteMobilityCategory();

  const totalItems = data?.totalItems ?? 0;
  const members = data?.member;

  return (
    <div className="flex flex-col gap-5">
      <TopbarActions>
        <RequirePermission permission={CREATE_PERMISSION}>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" aria-hidden="true" />
            Nieuwe categorie
          </Button>
        </RequirePermission>
      </TopbarActions>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Mobiliteitscategorieën
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Beheer de categorieën die je aan medewerkers kunt koppelen.
        </p>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <THead>
            <Tr>
              <Th>Naam</Th>
              <Th className="w-28">Status</Th>
              <Th className="w-24 text-right">
                <span className="sr-only">Acties</span>
              </Th>
            </Tr>
          </THead>
          <TBody
            aria-busy={isPlaceholderData}
            className={isPlaceholderData ? "opacity-60" : undefined}
          >
            <MobilityCategoryRows
              isPending={isPending}
              isError={isError}
              members={members}
              canUpdate={UPDATE_PERMISSION}
              canDelete={DELETE_PERMISSION}
              onEdit={setEditing}
              onDelete={setPendingDelete}
              onRetry={() => void refetch()}
            />
          </TBody>
        </Table>
      </Card>

      {totalItems > ITEMS_PER_PAGE && (
        <Pagination
          page={page}
          pageSize={ITEMS_PER_PAGE}
          totalItems={totalItems}
          status={isPending ? "pending" : isError ? "error" : "ready"}
          onPageChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, page: next }) })
          }
        />
      )}

      <MobilityCategorySheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        isSubmitting={createMutation.isPending}
        onSubmit={(values) => {
          createMutation.mutate(toMobilityCategoryInput(values), {
            onSuccess: () => setCreateOpen(false),
          });
        }}
      />

      <MobilityCategorySheet
        open={editing !== undefined}
        onOpenChange={(open) => {
          if (!open) setEditing(undefined);
        }}
        category={editing}
        isSubmitting={updateMutation.isPending}
        onSubmit={(values) => {
          if (!editing?.id) {
            toast.error("Bijwerken is mislukt. Probeer het opnieuw.");
            setEditing(undefined);
            return;
          }
          updateMutation.mutate(toMobilityCategoryInput(values), {
            onSuccess: () => setEditing(undefined),
          });
        }}
      />

      <Dialog
        open={pendingDelete !== undefined}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(undefined);
        }}
        title="Categorie verwijderen"
        description={
          pendingDelete
            ? `Weet je zeker dat je "${pendingDelete.name}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`
            : undefined
        }
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setPendingDelete(undefined)}
              disabled={deleteMutation.isPending}
            >
              Annuleren
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                const id = pendingDelete?.id;
                if (!id) {
                  toast.error("Verwijderen is mislukt. Probeer het opnieuw.");
                  return;
                }
                deleteMutation.mutate(id, {
                  onSuccess: () => setPendingDelete(undefined),
                });
              }}
            >
              <Trash2 className="size-4" aria-hidden="true" />
              Verwijderen
            </Button>
          </>
        }
      />
    </div>
  );
}

function MobilityCategoryRows({
  isPending,
  isError,
  members,
  canUpdate,
  canDelete,
  onEdit,
  onDelete,
  onRetry,
}: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly members: readonly MobilityCategory[] | undefined;
  readonly canUpdate: string;
  readonly canDelete: string;
  readonly onEdit: (category: MobilityCategory) => void;
  readonly onDelete: (category: MobilityCategory) => void;
  readonly onRetry: () => void;
}): ReactNode {
  if (isPending) {
    return SKELETON_ROW_KEYS.map((key) => (
      <Tr key={key}>
        <Td>
          <Skeleton className="h-4 w-32" />
        </Td>
        <Td>
          <Skeleton className="h-5 w-16 rounded-full" />
        </Td>
        <Td>
          <div className="flex justify-end gap-1">
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
          </div>
        </Td>
      </Tr>
    ));
  }

  if (isError) {
    return (
      <Tr>
        <Td colSpan={3} className="py-10 text-center">
          <div className="space-y-3">
            <p className="text-muted-foreground">
              We konden de categorieën niet laden.
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Opnieuw proberen
            </Button>
          </div>
        </Td>
      </Tr>
    );
  }

  if (members === undefined || members.length === 0) {
    return (
      <Tr>
        <Td colSpan={3} className="py-10 text-center text-muted-foreground">
          Nog geen mobiliteitscategorieën.
        </Td>
      </Tr>
    );
  }

  return members.map((category) => (
    <Tr key={category.id}>
      <Td className="font-medium">{category.name}</Td>
      <Td>
        {category.active ? (
          <Badge variant="success" size="sm">
            Actief
          </Badge>
        ) : (
          <Badge variant="neutral" size="sm">
            Inactief
          </Badge>
        )}
      </Td>
      <Td>
        <div className="flex justify-end gap-1">
          <RequirePermission permission={canUpdate}>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`${category.name} bewerken`}
              onClick={() => onEdit(category)}
            >
              <Pencil className="size-4" aria-hidden="true" />
            </Button>
          </RequirePermission>
          <RequirePermission permission={canDelete}>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`${category.name} verwijderen`}
              onClick={() => onDelete(category)}
            >
              <Trash2 className="size-4" aria-hidden="true" />
            </Button>
          </RequirePermission>
        </div>
      </Td>
    </Tr>
  ));
}
